import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi, userApi } from '../api'
import { takeMarketingConsent } from '../utils/marketingConsent'
import { SocialProvider } from '../constants/social'
import { useAuthStore } from '../stores/authStore'
import { toastUtil } from '../utils/toast'
import { isApp } from '../utils/platform'
import AppBar from '../components/AppBar'
import { REFERRAL_READY } from '../constants/featureFlags'
import { referralApi } from '../api/referral'
import SubmitButton from '../components/SubmitButton'

/**
 * 단계형 회원가입 마지막 구간 (아이디 → 닉네임).
 * 진입은 항상 휴대폰 인증(PhoneVerify) 뒤 — 약관도 그 전에 이미 동의됨.
 *  - 전화 신규:   /signup?phone=010...
 *  - 소셜 신규:   /signup?phone=010...&social=<base64 {provider, provider_id}>
 * 완료 시 register(phone + 아이디/닉네임 [+ 소셜]) → 로그인 → 온보딩/홈.
 */

const ERROR_COLOR = 'rgba(180, 5, 8, 0.8)' // #B40508 80%
const USERNAME_REGEX = /^[a-zA-Z0-9._]{3,20}$/
const isValidUsernameFormat = (u: string) =>
  USERNAME_REGEX.test(u) && !/[._]$/.test(u)

type CheckStatus = 'idle' | 'checking' | 'ok'

const Signup: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()
  const [searchParams] = useSearchParams()
  const { setToken, setUserId, getPostLoginPath, isAuthenticated } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)

  // 소셜 데이터(있으면) — 최종 가입 단계에서 register에 provider/provider_id로 전달
  const social = useMemo(() => {
    const raw = searchParams.get('social')
    if (!raw) return null
    try {
      return JSON.parse(atob(raw)) as { provider: string; provider_id: string }
    } catch {
      return null
    }
  }, [searchParams])

  // 인증 완료된 휴대폰번호 (전화·소셜 흐름 모두 이 단계엔 phone이 붙어 들어옴)
  const phone = searchParams.get('phone') || ''

  /*
   * 계정이 이미 만들어진 뒤 추천인 화면으로 돌아온 경우.
   *
   * 가입을 마치면 이 주소를 `step=referral` 로 바꿔 둔 채 권한 안내로 넘어간다.
   * 그래서 권한 안내에서 뒤로 가면 휴대폰 번호 화면이 아니라 여기(추천인 코드)로
   * 온다. 이때는 계정이 있으니 가입을 다시 하지 않고, 코드만 붙인다(서버가 가입 후
   * 1시간 안에만 허락한다).
   */
  const createdMode =
    REFERRAL_READY && searchParams.get('step') === 'referral' && isAuthenticated

  const [step, setStep] = useState(createdMode ? 2 : 0)

  // --- 1단계: 아이디 ---
  const [username, setUsername] = useState('')
  const [status, setStatus] = useState<CheckStatus>('idle')
  // 오류는 타이핑 중엔 숨기고, '다음으로'를 눌렀을 때만 표시
  const [showError, setShowError] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- 2단계: 닉네임(=이름, nickname 필드) ---
  const [nickname, setNickname] = useState('')
  const [showNickError, setShowNickError] = useState(false)
  const isNicknameValid =
    nickname.trim().length >= 1 && nickname.trim().length <= 15

  // --- 3단계: 추천인 코드(선택, 가입할 때만) — REFERRAL_READY 일 때만 나온다 ---
  const [referralCode, setReferralCode] = useState('')
  const [referralError, setReferralError] = useState('')
  const [checkingCode, setCheckingCode] = useState(false)

  useEffect(() => {
    setShowError(false)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const u = username.trim()
    // 3자 미만이거나 형식이 안 맞으면 라이브 오류 없이 조용히(idle)
    if (u.length < 3 || !isValidUsernameFormat(u)) {
      setStatus('idle')
      return
    }
    setStatus('checking')
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authApi.checkUsername({ username: u })
        setStatus(res.available ? 'ok' : 'idle')
      } catch {
        setStatus('idle')
      }
    }, 400)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [username])

  const handleNext = () => {
    if (step === 0) {
      if (status === 'ok') {
        setStep(1) // 아이디 통과 → 닉네임 단계
      } else {
        // 아직 사용 가능 확인 안 됨 → 눌렀을 때 오류 표시
        setShowError(true)
      }
    } else if (step === 1) {
      if (isNicknameValid) {
        // 약관은 이미 PhoneSignup에서 동의됨 → (추천인 코드 단계가 있으면 거기로) 계정 생성
        if (REFERRAL_READY) setStep(2)
        else handleComplete()
      } else {
        setShowNickError(true)
      }
    } else if (step === 2) {
      submitReferral()
    }
  }

  /** 계정이 이미 있을 때(권한 안내에서 돌아옴) 다음 화면으로 */
  const goPermissions = () => navigate('/permissions')

  /** 추천인 코드 '다음으로' — 비어 있으면 건너뛰기와 같다. 있으면 확인 후 가입한다 */
  const submitReferral = async () => {
    const code = referralCode.trim()
    if (createdMode) {
      if (!code) {
        goPermissions()
        return
      }
      if (checkingCode) return
      setCheckingCode(true)
      try {
        await referralApi.apply(code)
        goPermissions()
      } catch (e: any) {
        const msg: string | undefined = e?.response?.data?.message
        // 이미 추천인이 붙어 있으면 실패가 아니다 — 알리고 그대로 넘어간다
        if (msg === '이미 추천인 코드를 입력했어요') {
          toastUtil.info(msg)
          goPermissions()
          return
        }
        setReferralError(msg || '코드를 확인하지 못했어요. 잠시 후 다시 시도해주세요')
      } finally {
        setCheckingCode(false)
      }
      return
    }
    if (!code) {
      handleComplete()
      return
    }
    if (checkingCode || submitting) return
    setCheckingCode(true)
    try {
      const res = await referralApi.check(code)
      if (!res.valid) {
        setReferralError(res.message || '존재하지 않는 추천인 코드예요')
        return
      }
      handleComplete(code)
    } catch {
      setReferralError('코드를 확인하지 못했어요. 잠시 후 다시 시도해주세요')
    } finally {
      setCheckingCode(false)
    }
  }

  const handleComplete = async (referral?: string) => {
    if (submitting) return
    setSubmitting(true)
    try {
      // 전화·소셜 신규 모두 인증된 phone + 아이디/닉네임으로 계정 생성.
      // 소셜이면 provider/provider_id도 함께(소셜 연동). 비밀번호·이메일 없음(passwordless).
      const res = await authApi.register({
        phone: phone || undefined,
        username: username.trim(),
        nickname: nickname.trim(),
        ...(social
          ? {
              provider: social.provider as SocialProvider,
              provider_id: social.provider_id,
            }
          : {}),
        agree_terms: true,
        agree_privacy: true,
        // 약관 시트에서 고른 선택 항목. 안 골랐으면 false로 간다
        agree_marketing: takeMarketingConsent(),
        ...(referral ? { referral_code: referral } : {}),
      })

      if (res.token) setToken(res.token) // 네이티브 토큰 동기화 포함

      let contactConsent = false
      try {
        const profile = await userApi.getProfile()
        setUserId(profile.user.id)
        contactConsent = profile.user.contact_consent ?? false
      } catch {
        // 프로필 로드 실패해도 가입 자체는 완료 — 홈/온보딩으로 진행
      }

      // 신규 가입: 앱은 권한 안내(/permissions) → 연락처/알림 OS 권한 → 친구추천/온보딩.
      // 웹은 권한 개념이 없으므로 기존 경로(홈)로.
      if (REFERRAL_READY && isApp()) {
        // 이 화면을 '추천인 단계(가입 완료)'로 바꿔 둔 뒤 권한 안내로 넘어간다.
        // 권한 안내에서 뒤로 가면 휴대폰 번호 화면이 아니라 추천인 화면이 나온다.
        const p = new URLSearchParams(searchParams)
        p.set('step', 'referral')
        navigate(`/signup?${p.toString()}`, { replace: true })
        navigate('/permissions')
        return
      }
      navigate(
        isApp() ? '/permissions' : getPostLoginPath(contactConsent),
        { replace: true }
      )
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || '회원가입 중 오류가 발생했습니다'
      toastUtil.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setShowNickError(false)
      setReferralError('')
      setStep(step - 1) // 추천인 → 닉네임 → 아이디
    } else {
      navigate(-1) // 아이디 → 이전 화면
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* 뒤로가기 (앱 표준 AppBar, 타이틀 없음) */}
        {/* 계정이 이미 만들어졌으면 아이디·이름 단계로 돌아갈 수 없다 — 뒤로가기를 숨긴다 */}
        <AppBar onBack={handleBack} showBack={!createdMode} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-40px">
          {step === 0 && (
            <>
              <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
                아이디를 입력해주세요
              </p>

              {/* 밑줄형 입력 (@ 접두, 굵게 / placeholder 없음) */}
              <div className="mt-32px">
                <div className="flex items-center">
                  <span
                    className="font-pretendard font-bold text-22px leading-135%"
                    style={{
                      color: showError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
                    }}
                  >
                    @
                  </span>
                  <input
                    value={username}
                    onChange={(e) =>
                      setUsername(
                        e.target.value.replace(/[^a-zA-Z0-9._]/g, '').slice(0, 20)
                      )
                    }
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="flex-1 bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% py-8px"
                    style={{
                      color: showError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
                    }}
                  />
                </div>
                <div
                  className="h-2px w-full"
                  style={{
                    backgroundColor: showError
                      ? ERROR_COLOR
                      : 'rgba(75, 54, 33, 0.2)',
                  }}
                />

                {showError ? (
                  <div className="flex items-center gap-8px mt-12px">
                    <img
                      src="/images/signup-x.png"
                      alt=""
                      className="w-20px h-20px object-contain"
                    />
                    <span
                      className="font-pretendard font-bold text-15px"
                      style={{ color: ERROR_COLOR }}
                    >
                      사용할 수 없는 아이디입니다
                    </span>
                  </div>
                ) : status === 'ok' ? (
                  <div className="flex items-center gap-8px mt-12px">
                    <img
                      src="/images/signup-check.png"
                      alt=""
                      className="w-20px h-20px object-contain"
                    />
                    <span className="font-pretendard font-bold text-15px text-textColorText3">
                      사용할 수 있는 아이디입니다
                    </span>
                  </div>
                ) : null}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
                추천인 코드를 입력해주세요
              </p>

              <div className="mt-32px">
                <input
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 15).toUpperCase())
                    if (referralError) setReferralError('')
                  }}
                  placeholder="TASTEE-XXXX"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% py-8px placeholder:text-472410-20%"
                  style={{
                    color: referralError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
                  }}
                />
                <div
                  className="h-2px w-full"
                  style={{
                    backgroundColor: referralError
                      ? ERROR_COLOR
                      : 'rgba(75, 54, 33, 0.2)',
                  }}
                />

                {referralError && (
                  <div className="flex items-center gap-8px mt-12px">
                    <img
                      src="/images/signup-x.png"
                      alt=""
                      className="w-20px h-20px object-contain"
                    />
                    <span
                      className="font-pretendard font-bold text-15px"
                      style={{ color: ERROR_COLOR }}
                    >
                      {referralError}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <p className="font-pretendard font-bold text-22px leading-135% text-brown-80%-main">
                이름을 입력해주세요
              </p>

              <div className="mt-32px">
                <input
                  value={nickname}
                  onChange={(e) => {
                    setNickname(e.target.value.slice(0, 15))
                    if (showNickError) setShowNickError(false)
                  }}
                  maxLength={15}
                  className="w-full bg-transparent border-none outline-none font-pretendard font-bold text-22px leading-135% py-8px"
                  style={{
                    color: showNickError ? ERROR_COLOR : 'rgba(75, 54, 33, 0.8)',
                  }}
                />
                <div
                  className="h-2px w-full"
                  style={{
                    backgroundColor: showNickError
                      ? ERROR_COLOR
                      : 'rgba(75, 54, 33, 0.2)',
                  }}
                />

                {showNickError && (
                  <div className="flex items-center gap-8px mt-12px">
                    <img
                      src="/images/signup-x.png"
                      alt=""
                      className="w-20px h-20px object-contain"
                    />
                    <span
                      className="font-pretendard font-bold text-15px"
                      style={{ color: ERROR_COLOR }}
                    >
                      15자 이내로 입력해주세요
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 하단 다음으로 (항상 활성 — 눌렀을 때 검증) */}
        <div className="w-full px-20px">
          <SubmitButton
            text="다음으로"
            onSubmit={handleNext}
            onDisabled={handleNext}
            loading={submitting || checkingCode}
          />
          {/* 추천인 코드는 선택 — 없으면 건너뛴다(Figma 친구 초대 흐름 5번) */}
          {step === 2 && (
            <button
              type="button"
              onClick={() => (createdMode ? goPermissions() : handleComplete())}
              disabled={submitting || checkingCode}
              className="w-full pt-20px font-pretendard font-bold text-16px text-brown-80%-main disabled:opacity-50"
            >
              건너뛰기
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Signup
